import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getUserById } from '../services/userService';
import ProfilePhotoSetup from '../components/ProfilePhotoSetup';

const ProfileEditPage = ({ user }) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [profile, setProfile] = useState(null);
    const [displayName, setDisplayName] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingPhoto, setEditingPhoto] = useState(searchParams.get('photo') === '1');
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        getUserById(user.uid)
            .then((userProfile) => {
                if (!active) return;
                setProfile(userProfile);
                setDisplayName(userProfile?.name || userProfile?.displayName || user.displayName || '');
            })
            .catch((loadError) => {
                console.error('Failed to load profile for editing:', loadError);
                if (active) setError('Could not load your profile. Please try again.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [user.uid, user.displayName]);

    const closePhotoEditor = () => {
        setEditingPhoto(false);
        setSearchParams({}, { replace: true });
    };

    const handlePhotoComplete = async () => {
        closePhotoEditor();
        try {
            setProfile(await getUserById(user.uid));
        } catch (loadError) {
            console.error('Failed to refresh profile photo:', loadError);
        }
    };

    const handleSave = async (event) => {
        event.preventDefault();
        const name = displayName.trim();
        if (name.length < 2 || name.length > 50) {
            setError('Name must be between 2 and 50 characters.');
            return;
        }
        if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
            setError('Please sign in again before editing your profile.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            await updateDoc(doc(db, 'users', user.uid), {
                name,
                fullName: name,
                displayName: name,
                nameLower: name.toLowerCase(),
            });
            navigate(`/profile/${user.uid}`, { replace: true });
        } catch (saveError) {
            console.error('Failed to update profile name:', saveError);
            setError('Could not save your name. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (editingPhoto) {
        return (
            <ProfilePhotoSetup
                user={user}
                onComplete={handlePhotoComplete}
                onBack={closePhotoEditor}
            />
        );
    }

    if (loading) {
        return <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">Loading profile...</div>;
    }

    return (
        <main className="min-h-screen bg-[#0e0e0e] px-4 py-8 text-white">
            <div className="mx-auto w-full max-w-md">
                <button
                    type="button"
                    onClick={() => navigate(`/profile/${user.uid}`)}
                    className="mb-6 text-sm text-white/70 hover:text-white"
                >
                    Back to profile
                </button>

                <h1 className="mb-8 text-2xl font-bold">Edit profile</h1>

                {error && <p role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}

                <button
                    type="button"
                    onClick={() => {
                        setSearchParams({ photo: '1' });
                        setEditingPhoto(true);
                    }}
                    aria-label="Edit profile picture"
                    className="mb-8 flex w-full flex-col items-center gap-3"
                >
                    <span className="h-28 w-28 overflow-hidden rounded-full bg-white/10 ring-2 ring-white/20">
                        {profile?.photoURL ? (
                            <img src={profile.photoURL} alt="Profile" className="h-full w-full object-cover" />
                        ) : (
                            <span className="flex h-full w-full items-center justify-center text-4xl font-bold">
                                {(displayName || 'U').charAt(0).toUpperCase()}
                            </span>
                        )}
                    </span>
                    <span className="text-sm font-semibold text-red-300">Change profile picture</span>
                </button>

                <form onSubmit={handleSave} className="space-y-5">
                    <div>
                        <label htmlFor="profile-display-name" className="mb-2 block text-sm text-white/70">Name</label>
                        <input
                            id="profile-display-name"
                            type="text"
                            value={displayName}
                            onChange={(event) => setDisplayName(event.target.value)}
                            maxLength={50}
                            required
                            className="w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-white outline-none focus:border-red-400"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={saving || displayName.trim().length < 2}
                        className="w-full rounded-lg bg-red-600 px-4 py-3 font-semibold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save name'}
                    </button>
                </form>
            </div>
        </main>
    );
};

export default ProfileEditPage;